import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, Paper, Typography } from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";
import GetAppIcon from "@material-ui/icons/GetApp";
import { Toaster } from "@/components";
import {
  getAssistedAffiliationDraftService,
  getAssistedAffiliationListService,
  getDraftErrorMessage,
  getValidateIdentificationErrorMessage,
  rejectAssistedAffiliationDraftService,
  updateAssistedAffiliationDraftService,
  validateIdentificationChangesService,
} from "@/services/assistedAffiliation";
import AssistedAffiliationFilterBar from "./components/AssistedAffiliationFilterBar";
import AssistedAffiliationManualRequest from "./components/AssistedAffiliationManualRequest";
import AssistedAffiliationTable from "./components/AssistedAffiliationTable";
import ValidationChangesModal from "./components/ValidationChangesModal";
import {
  DRAFT_LOAD_ERROR_MESSAGE,
  EMPTY_FILTERS,
  LIST_LOAD_ERROR_MESSAGE,
  METABASE_EXPORT_URL,
  buildValidationChanges,
  CLEAN_VALIDATIONS,
  PAGE_SIZE_OPTIONS,
  REQUESTS_CHUNK_SIZE,
  SEARCH_DEBOUNCE_MS,
} from "./constants";
import { draftToForm, formToDraft } from "./draftMappers";
import {
  filtersToListFilters,
  hasListFilters,
  sameListFilters,
} from "./filterMappers";
import type {
  AssistedAffiliationDraft,
  AssistedAffiliationListFilters,
} from "@/services/assistedAffiliation/types";
import type {
  AlertState,
  AssistedAffiliationFilters,
  AssistedAffiliationForm,
  AssistedAffiliationRequest,
  AssistedAffiliationRncScreenProps,
  ValidationChange,
} from "./types";
import useStyle from "./styles";

const AssistedAffiliationRncScreen = ({
  readOnly = false,
}: AssistedAffiliationRncScreenProps) => {
  const classes = useStyle();
  const [filters, setFilters] =
    useState<AssistedAffiliationFilters>(EMPTY_FILTERS);
  const [isManualRequestOpen, setIsManualRequestOpen] = useState(false);
  const [editedDraft, setEditedDraft] = useState<AssistedAffiliationDraft | null>(
    null
  );
  const [isLoadingDraft, setIsLoadingDraft] = useState(false);
  const [isSavingDraft, setIsSavingDraft] = useState(false);

  // La traducción depende solo del registro traído, así que se rehace cuando ese cambia y no en
  // cada render de la bandeja.
  const editedForm = useMemo(
    () => (editedDraft ? draftToForm(editedDraft) : null),
    [editedDraft]
  );
  const [requests, setRequests] = useState<AssistedAffiliationRequest[]>([]);
  // Lo que muestra la barra y lo que se consultó son dos cosas distintas: esto último es lo que
  // viaja en el body, y es también lo que necesitará la exportación de resultados.
  const [listFilters, setListFilters] = useState<AssistedAffiliationListFilters>(
    {}
  );
  // Sube con cada consulta nueva: una respuesta que llega con un número viejo ya no se pinta.
  const querySequenceRef = useRef(0);
  const [totalElements, setTotalElements] = useState(0);
  const [currentChunk, setCurrentChunk] = useState(0);
  const [listRevision, setListRevision] = useState(0);
  const [pageSize, setPageSize] = useState(PAGE_SIZE_OPTIONS[0]);
  const [loadFailed, setLoadFailed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [validatingRequestId, setValidatingRequestId] = useState<string | null>(
    null
  );
  const [selectedRequest, setSelectedRequest] =
    useState<AssistedAffiliationRequest | null>(null);
  const [validationChanges, setValidationChanges] = useState<ValidationChange[]>(
    []
  );
  const [isValidationChangesModalOpen, setIsValidationChangesModalOpen] =
    useState(false);
  const [alert, setAlert] = useState<AlertState>({
    show: false,
    type: "info",
    text: "",
  });

  const openManualRequest = useCallback(() => {
    setIsManualRequestOpen(true);
  }, []);

  const closeValidationChangesModal = useCallback(() => {
    setIsValidationChangesModalOpen(false);
    setSelectedRequest(null);
    setValidationChanges([]);
  }, []);

  const handleCommerceCreated = useCallback(() => {
    setIsManualRequestOpen(false);
    setEditedDraft(null);
    setRequests([]);
    setTotalElements(0);
    setCurrentChunk(0);
    setListRevision((revision) => revision + 1);
    setAlert({
      show: true,
      type: "success",
      text: "Solicitud creada exitosamente.",
    });
  }, []);

  // Editar cuesta una consulta: el listado no trae los campos que el formulario necesita.
  const openEditForm = useCallback(
    async (request: AssistedAffiliationRequest) => {
      setIsLoadingDraft(true);
      try {
        const draft = await getAssistedAffiliationDraftService(
          request.identification
        );
        setEditedDraft(draft);
      } catch (error) {
        setAlert({
          show: true,
          type: "error",
          text: getDraftErrorMessage(error, DRAFT_LOAD_ERROR_MESSAGE),
        });
      } finally {
        setIsLoadingDraft(false);
      }
    },
    []
  );

  const rejectSelectedRequest = useCallback(async () => {
    if (selectedRequest) {
      try {
        await rejectAssistedAffiliationDraftService(selectedRequest.identification);
      } catch (error) {
        setAlert({
          show: true,
          type: "error",
          text: getDraftErrorMessage(
            error,
            "No se pudo rechazar la solicitud."
          ),
        });
        return;
      }

      setRequests((current) =>
        current.map((request) =>
          request.id === selectedRequest.id
            ? { ...request, status: "REQUEST_CANCELLED" }
            : request
        )
      );
    }

    closeValidationChangesModal();
  }, [closeValidationChangesModal, selectedRequest]);

  const reviewSelectedRequest = useCallback(() => {
    // Se lee antes de cerrar, porque cerrar es justo lo que limpia la solicitud seleccionada.
    const request = selectedRequest;
    closeValidationChangesModal();
    if (request) openEditForm(request);
  }, [closeValidationChangesModal, openEditForm, selectedRequest]);

  const handleEditRequest = useCallback(
    async (request: AssistedAffiliationRequest) => {
      const previousValidations = request.validations || CLEAN_VALIDATIONS;

      setValidatingRequestId(request.id);

      try {
        const currentValidations = await validateIdentificationChangesService({
          documentNumber: request.identification,
          lastValidationAt: request.lastValidationAt,
          previousValidations,
        });
        const validatedAt = new Date().toISOString();
        const updatedRequest: AssistedAffiliationRequest = {
          ...request,
          validations: currentValidations,
          lastValidationAt: validatedAt,
        };
        const changes = buildValidationChanges(
          previousValidations,
          currentValidations
        );

        setRequests((current) =>
          current.map((item) =>
            item.id === request.id ? updatedRequest : item
          )
        );

        if (changes.length === 0) {
          await openEditForm(updatedRequest);
          return;
        }

        setSelectedRequest(updatedRequest);
        setValidationChanges(changes);
        setIsValidationChangesModalOpen(true);
      } catch (error) {
        setAlert({
          show: true,
          type: "error",
          text: getValidateIdentificationErrorMessage(error),
        });
      } finally {
        setValidatingRequestId(null);
      }
    },
    [openEditForm]
  );

  // La barra reporta cada tecla y cada consulta cuesta una petición: se espera a que el usuario
  // termine de escribir y se consulta solo cuando el filtro efectivo cambió. Un término corto, una
  // fecha a medio teclear o cambiar el criterio sin texto no mueven nada. Volver a la consulta sin
  // filtros, que es lo que hace "Limpiar filtros", no espera.
  useEffect(() => {
    const nextListFilters = filtersToListFilters(filters);
    if (sameListFilters(nextListFilters, listFilters)) return undefined;

    const applyFilters = () => {
      querySequenceRef.current += 1;
      setListFilters(nextListFilters);
      setRequests([]);
      setTotalElements(0);
      setCurrentChunk(0);
      setLoadFailed(false);
      // Sin esto el estado vacío parpadea entre borrar lo acumulado y que llegue el primer bloque.
      setLoading(true);
      // El spinner desmonta la tabla, y la tabla guarda por su cuenta cuántas filas muestra: al
      // volver, arranca otra vez en la primera opción. Si aquí no se hace lo mismo, la pantalla cree
      // que siguen siendo 100 por página y pide bloques que ya tiene.
      setPageSize(PAGE_SIZE_OPTIONS[0]);
    };

    if (!hasListFilters(nextListFilters)) {
      applyFilters();
      return undefined;
    }

    const timer = setTimeout(applyFilters, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [filters, listFilters]);

  // La bandeja trae bloques bajo demanda en vez de todo el listado, y los acumula: la tabla
  // rebana localmente lo acumulado mientras el pie cuenta con el total del servidor.
  useEffect(() => {
    // El número se toma al empezar la consulta; si cambia mientras la respuesta viaja es que el
    // usuario ya pidió otra cosa. Sube por consulta y no por bloque, así los bloques de una misma
    // consulta siguen acumulándose.
    const querySequence = querySequenceRef.current;
    const isCurrentQuery = () => querySequence === querySequenceRef.current;

    const fetchChunk = async () => {
      // El spinner es solo para la primera carga; los bloques siguientes llegan sin interrumpir
      // lo que el usuario ya está viendo.
      setLoading(currentChunk === 0);
      try {
        const response = await getAssistedAffiliationListService({
          pageNumber: currentChunk,
          pageSize: REQUESTS_CHUNK_SIZE,
          ...listFilters,
        });
        if (!isCurrentQuery()) return;
        // Ante una respuesta con otra forma, la bandeja queda vacía en lugar de romperse.
        const items = Array.isArray(response.items) ? response.items : [];
        setRequests((previous) => [...previous, ...items]);
        setTotalElements(response.total_elements ?? 0);
      } catch (error) {
        if (!isCurrentQuery()) return;
        // Una consulta que falla deja la tabla tan vacía como una sin coincidencias, pero afirmar que
        // no existen registros sería falso: no se llegó a saber. La bandeja vuelve a su mensaje neutro.
        setLoadFailed(true);
        setAlert({
          show: true,
          type: "error",
          text: getDraftErrorMessage(error, LIST_LOAD_ERROR_MESSAGE),
        });
      } finally {
        // El spinner pertenece a la consulta vigente: una respuesta tardía no debe apagarlo.
        if (isCurrentQuery()) setLoading(false);
      }
    };

    fetchChunk();
  // Tres disparadores, independientes entre sí: el bloque siguiente, un filtro distinto y el
  // refresco que pide la pantalla tras crear un comercio.
  }, [currentChunk, listFilters, listRevision]);

  // Se pide el bloque siguiente solo si lo acumulado no alcanza para la página que sigue a la
  // que el usuario abre. Retroceder nunca lo dispara.
  const handleNextPage = (newPage = 0) => {
    if (requests.length >= totalElements) return;
    if (requests.length < (newPage + 2) * pageSize) {
      setCurrentChunk((chunk) => chunk + 1);
    }
  };

  // El endpoint reemplaza el registro entero, y el formulario solo cubre parte de sus campos: por
  // eso el payload sale de la respuesta original y no se construye desde el formulario.
  // Recibe el registro en vez de leerlo del estado: así no hace falta comprobar aquí algo que la
  // rama de edición ya garantizó. El error sube al formulario, que es quien avisa mientras se edita.
  const handleSaveEditedRequest = async (
    draft: AssistedAffiliationDraft,
    form: AssistedAffiliationForm
  ) => {
    setIsSavingDraft(true);
    try {
      const payload = formToDraft(draft, form);
      const saved = await updateAssistedAffiliationDraftService(payload);
      // Lo enviado queda como base de la siguiente edición. `updated_at` lo asigna el servidor.
      setEditedDraft({
        ...payload,
        updated_at: saved?.updated_at ?? payload.updated_at,
      });
    } finally {
      setIsSavingDraft(false);
    }
  };

  if (editedDraft && editedForm) {
    return (
      <Paper className={classes.paper} elevation={1}>
        <AssistedAffiliationManualRequest
          onBack={() => setEditedDraft(null)}
          onCreated={handleCommerceCreated}
          edit={{
            defaultForm: editedForm,
            lastUpdatedAt: editedDraft.updated_at,
            isSaving: isSavingDraft,
            onSave: (form) => handleSaveEditedRequest(editedDraft, form),
          }}
        />
      </Paper>
    );
  }

  if (isManualRequestOpen) {
    return (
      <Paper className={classes.paper} elevation={1}>
        <AssistedAffiliationManualRequest
          onBack={() => setIsManualRequestOpen(false)}
          onCreated={handleCommerceCreated}
        />
      </Paper>
    );
  }

  return (
    <Paper className={classes.paper} elevation={1}>
      <div className={classes.header}>
        <Typography variant="h5" component="h2" className={classes.title}>
          Afiliación Asistida
        </Typography>
        <div className={classes.headerActions}>
          <Button
            variant="contained"
            className={classes.primaryButton}
            startIcon={<AddIcon />}
            disabled={readOnly}
            onClick={openManualRequest}
          >
            Nueva solicitud
          </Button>
          <Button
            variant="outlined"
            className={classes.secondaryButton}
            startIcon={<GetAppIcon />}
            onClick={() =>
              window.open(
                METABASE_EXPORT_URL,
                "_blank",
                "noopener,noreferrer"
              )
            }
          >
            Exportar
          </Button>
        </div>
      </div>

      <AssistedAffiliationFilterBar
        filters={filters}
        onFiltersChange={setFilters}
      />

      <AssistedAffiliationTable
        requests={requests}
        readOnly={readOnly}
        loading={loading || isLoadingDraft}
        hasActiveFilters={hasListFilters(listFilters) && !loadFailed}
        onClearFilters={() => setFilters(EMPTY_FILTERS)}
        validatingRequestId={validatingRequestId}
        totalElements={totalElements}
        onNextPage={handleNextPage}
        onPageSizeChange={setPageSize}
        onEdit={handleEditRequest}
      />

      <ValidationChangesModal
        isOpen={isValidationChangesModalOpen}
        changes={validationChanges}
        onAcknowledge={rejectSelectedRequest}
        onCancel={closeValidationChangesModal}
        onReview={reviewSelectedRequest}
      />

      <Toaster
        show={alert.show}
        type={alert.type}
        text={alert.text}
        onClose={() => setAlert((current) => ({ ...current, show: false }))}
      />
    </Paper>
  );
};

export default AssistedAffiliationRncScreen;
